import { distance as turfDistance, length as turfLength, lineString as turfLineString, point as turfPoint, simplify as turfSimplify } from '@turf/turf';
import { URLs } from 'src/constants/url';

// Module-level Directions cache shared by every tracking view (Track detail map,
// useRadarData distance hook, ShareRadarOrder). Route refetches fire on every
// orderDetail refresh/remount with identical waypoints — serving repeats from here
// avoids re-hitting Mapbox and keeps the distance cards consistent with the drawn route.
const routeCache = new Map<string, DrivingRouteResult>();
const ROUTE_CACHE_MAX = 20;

// In-flight dedupe: on page load the map draw AND the total-distance hook request the
// SAME route at the same instant — both miss the (empty) cache and each fan out their
// own set of Directions requests. Sharing the pending promise halves the network work
// and the time-to-first-route.
const pendingRoutes = new Map<string, Promise<DrivingRouteResult>>();

// Very long stitched routes (max-distance pairwise splits) can come back with 100k+
// coordinates. Everything downstream is O(coords) or worse — marker snapping,
// bounds fitting, and especially the per-animation-frame turf.lineSliceAlong calls —
// which is what made huge routes take seconds to appear. Simplify to a renderable
// size; at the continental zoom these routes are viewed at, the difference is invisible.
const MAX_RENDER_COORDS = 10000;

const simplifyForRender = (coords: [number, number][]): [number, number][] => {
    if (coords.length <= MAX_RENDER_COORDS) return coords;

    try {
        let simplified = coords;
        // Start at ~50m deviation and coarsen until the route is small enough
        // (one pass is almost always sufficient).
        let tolerance = 0.0005;

        while (simplified.length > MAX_RENDER_COORDS && tolerance <= 0.016) {
            simplified = turfSimplify(turfLineString(coords), { tolerance, highQuality: false }).geometry.coordinates as [number, number][];
            tolerance *= 4;
        }

        return simplified.length >= 2 ? simplified : coords;
    } catch {
        // Simplification is a perf optimization only — never fail the route over it
        return coords;
    }
};

export interface DrivingRouteResult {
    /** Render polyline — simplified to MAX_RENDER_COORDS for huge routes (see simplifyForRender). */
    coords: [number, number][];
    /** Mapbox's own route distance (sum of segment `distance`), independent of simplification. */
    distanceMiles: number;
    /**
     * Unsimplified stitched polyline for MEASURING (Remaining Distance). Simplification cuts
     * corners, so a turf length over `coords` reads a mile or so short of WAL — which measures
     * the raw Directions geometry. Same reference as `coords` when nothing was simplified.
     */
    measureCoords: [number, number][];
}

/**
 * Remaining miles from the truck to the destination measured along the route — WAL Radar's
 * remaining-distance rule: snap the truck to the nearest route vertex, take the polyline from
 * there to the end, and return its turf length (2 decimals). Returns null when fewer than two
 * vertices remain (truck is at the destination) so callers can treat that as 0.
 * Always pass the UNSIMPLIFIED `measureCoords` so the figure matches WAL for the same order.
 */
export const getRemainingRouteMiles = (routeCoords: [number, number][], truckLngLat: [number, number]): number | null => {
    if (!routeCoords || routeCoords.length < 2) return null;

    const truck = turfPoint(truckLngLat);
    let closestIdx = 0;
    let minDist = Infinity;

    routeCoords.forEach((coord, idx) => {
        const d = turfDistance(truck, turfPoint(coord), { units: 'meters' });

        if (d < minDist) {
            minDist = d;
            closestIdx = idx;
        }
    });

    const remaining = routeCoords.slice(closestIdx);

    if (remaining.length < 2) return null;

    return parseFloat(turfLength(turfLineString(remaining), { units: 'miles' }).toFixed(2));
};

/**
 * Fetch a driving route from the Mapbox Directions API through start → waypoints → end.
 *
 * Ported from wal-frontend-v2 Radar detail (getDrivingRouteCoordinatesWithMultiplePoints)
 * so the customer portal handles the same edge cases the WAL radar already does:
 * - consecutive duplicate waypoints are dropped (parked-truck pings inflate the count)
 * - requests are split into overlapping segments of 25 coordinates (Mapbox hard limit)
 * - "Route exceeds maximum distance limitation" (InvalidInput) and NoRoute responses with
 *   3+ waypoints are retried as pairwise requests and stitched back together, so long or
 *   partially-unroutable trips still draw instead of failing the whole route
 * - exclude=country_border is retried without the exclude when it yields no route
 */
export const getDrivingRouteWithWaypoints = async (
    start: { lat: number; lng: number },
    points: { lat: number; lng: number }[],
    end: { lat: number; lng: number },
    preventCrossBorder: boolean = false,
    accessToken?: string
): Promise<DrivingRouteResult> => {
    const token = accessToken || (window as any)?.MAPBOX_ACCESSTOKEN;
    if (!token) return { coords: [], distanceMiles: 0, measureCoords: [] };

    const excludeParam = preventCrossBorder ? '&exclude=country_border' : '';
    // Location updates arrive newest-first from the API — reverse so the route runs
    // start → oldest ping → … → newest ping → end (same as WAL radar).
    const reversedPoints = points?.length > 0 ? [...points].reverse() : [];

    // Combine all points into one ordered array: start → waypoints → end.
    // Consecutive duplicate waypoints (same coordinate to ~1m / 5 decimals) are dropped:
    // tracking pings often repeat the same location (parked truck, daily carrier pings),
    // and each duplicate needlessly inflates the waypoint count — pushing the list past
    // the 25-coordinate limit and into extra Directions requests — without changing the
    // route geometry at all.
    const rawPoints = [start, ...reversedPoints, end];
    const pointKey = (p: { lat: number; lng: number }) => `${Number(p.lng).toFixed(5)},${Number(p.lat).toFixed(5)}`;
    const dedupedPoints = rawPoints.filter((p, i) => i === 0 || pointKey(p) !== pointKey(rawPoints[i - 1]));
    // Never dedupe below 2 points (start ≈ end with no distinct pings): Directions always
    // needs at least start;end, and the map draws markers off the result.
    const allPoints = dedupedPoints.length >= 2 ? dedupedPoints : [rawPoints[0], rawPoints[rawPoints.length - 1]];

    const cacheKey = `${preventCrossBorder ? 'x' : 'o'}|${allPoints.map(pointKey).join(';')}`;
    const cached = routeCache.get(cacheKey);
    if (cached) return cached;

    // Same route already being fetched by another caller — await its result instead
    // of duplicating the whole Directions fan-out.
    const pending = pendingRoutes.get(cacheKey);
    if (pending) return pending;

    const routePromise = (async (): Promise<DrivingRouteResult> => {
        // Mapbox Directions API hard limit is 25 coordinates per request.
        // When total points exceed 25, split into overlapping segments of 25
        // (overlap by 1 point at each boundary so segments connect seamlessly),
        // fetch all segments in parallel, then stitch the coordinate arrays together.
        const MAX_COORDS = 25;

        const buildSegmentUrl = (segPoints: { lat: number; lng: number }[], exclude: string) => {
            const coordStr = segPoints.map((p) => `${p.lng},${p.lat}`).join(';');

            return `${URLs.MAP_BOX_API}/directions/v5/mapbox/driving/${coordStr}?geometries=geojson&language=en&overview=full${exclude}&access_token=${token}`;
        };

        // Fetch with the border-free constraint; if that yields no route (waypoints span countries,
        // making exclude=country_border infeasible), retry once without the exclude so the route
        // still draws instead of the line silently disappearing.
        const fetchWithBorderRetry = async (segPoints: { lat: number; lng: number }[]): Promise<any> => {
            const resp = await fetch(buildSegmentUrl(segPoints, excludeParam)).then((r) => r.json());

            if (excludeParam && !resp?.routes?.[0]) {
                // Tag fallback responses so they are drawn but NEVER cached — a transient
                // failure of the border-free attempt must stay retryable on the next call
                // (caching it would pin the cross-border variant for the whole session).
                const fallback = await fetch(buildSegmentUrl(segPoints, '')).then((r) => r.json());

                return fallback && typeof fallback === 'object' ? { ...fallback, __usedBorderFallback: true } : fallback;
            }

            return resp;
        };

        // Fetch a single segment. Mapbox driving has a ~10,000 km max distance per request.
        // When that limit is hit with 3+ waypoints, re-fetch the segment as N-1 pairwise
        // requests in parallel and stitch the results back into a single routes[0]-shaped
        // object so the outer stitcher doesn't need to know about the retry. NoRoute with
        // 3+ waypoints gets the same treatment: ONE unroutable hop (island/ferry-only stop,
        // bad geocode) fails the whole request — split pairwise so the unroutable hop is
        // skipped and the reachable remainder still draws.
        // __cacheable is true only when the result is PRISTINE (first attempt, no border
        // fallback, no gaps) so transient failures stay retryable instead of pinning a
        // gapped route for the session.
        const fetchSegment = async (segPoints: { lat: number; lng: number }[]): Promise<any> => {
            const resp = await fetchWithBorderRetry(segPoints);
            const isDistanceExceeded = resp?.code === 'InvalidInput' && typeof resp?.message === 'string' && resp.message.toLowerCase().includes('maximum distance');
            const isUnroutable = resp?.code === 'NoRoute';

            if ((!isDistanceExceeded && !isUnroutable) || segPoints.length <= 2) {
                return resp && typeof resp === 'object' ? { ...resp, __cacheable: Boolean(resp?.routes?.[0]) && !resp?.__usedBorderFallback } : { routes: [], __cacheable: false };
            }

            const pairs: { lat: number; lng: number }[][] = [];

            for (let i = 0; i < segPoints.length - 1; i++) {
                pairs.push([segPoints[i], segPoints[i + 1]]);
            }

            const pairResults = await Promise.all(pairs.map((p) => fetchWithBorderRetry(p)));
            let stitched: [number, number][] = [];
            let distance = 0;
            pairResults.forEach((pr, i) => {
                if (pr?.routes?.[0]) {
                    distance += pr.routes[0].distance || 0;
                    const c: [number, number][] = pr.routes[0].geometry.coordinates;
                    stitched = [...stitched, ...(i === 0 ? c : c.slice(1))];
                }
            });

            // A pair answering NoRoute is a DETERMINISTIC Mapbox verdict (that hop is simply
            // not drivable) — the stitched remainder is stable and safe to cache. Anything
            // else missing routes[0] (rate limit, network blip) keeps the result uncacheable
            // so the next call retries.
            const allPairsPristine = pairResults.every((pr) => (pr?.routes?.[0] || pr?.code === 'NoRoute') && !pr?.__usedBorderFallback);

            return stitched.length ? { routes: [{ geometry: { coordinates: stitched }, distance }], __cacheable: allPairsPristine } : { routes: [], __cacheable: false };
        };

        // Build segments — each overlaps the previous by 1 point (the boundary) so
        // the route stitches continuously without gaps between chunks.
        const segments: { lat: number; lng: number }[][] = [];

        for (let i = 0; i < allPoints.length - 1; i += MAX_COORDS - 1) {
            segments.push(allPoints.slice(i, i + MAX_COORDS));
        }

        try {
            const results = await Promise.all(segments.map((seg) => fetchSegment(seg)));

            let stitchedCoords: [number, number][] = [];
            let totalDistanceMeters = 0;

            results.forEach((data, i) => {
                if (data?.routes?.[0]) {
                    totalDistanceMeters += data.routes[0].distance || 0;
                    const coords: [number, number][] = data.routes[0].geometry.coordinates;
                    // Skip the first coord of every segment after the first to avoid duplicating the boundary point
                    stitchedCoords = [...stitchedCoords, ...(i === 0 ? coords : coords.slice(1))];
                }
            });

            const distanceMiles = totalDistanceMeters ? parseFloat((totalDistanceMeters / 1609.344).toFixed(2)) : 0;
            // Distance always comes from the API sums above (BEFORE simplification), so the
            // Total Distance card is unaffected by the render-side coordinate reduction.
            const result: DrivingRouteResult = { coords: simplifyForRender(stitchedCoords), distanceMiles, measureCoords: stitchedCoords };

            // Cache only COMPLETE, first-attempt routes: every segment must be __cacheable
            // (routed, no gaps, no border fallback). Empty or partial results usually mean a
            // transient Mapbox failure and must stay retryable on the next call — the drawn
            // route is still best-effort either way, only the cache write is stricter.
            // FIFO-evict the oldest entry (Map iterates in insertion order) at the cap.
            if (stitchedCoords.length > 0 && results.every((r) => r?.__cacheable)) {
                if (routeCache.size >= ROUTE_CACHE_MAX) {
                    const oldestKey = routeCache.keys().next().value;

                    if (oldestKey !== undefined) routeCache.delete(oldestKey);
                }
                routeCache.set(cacheKey, result);
            }

            return result;
        } catch {
            return { coords: [], distanceMiles: 0, measureCoords: [] };
        }
    })();

    pendingRoutes.set(cacheKey, routePromise);

    try {
        return await routePromise;
    } finally {
        pendingRoutes.delete(cacheKey);
    }
};
